// src/pages/admin/AdminUsersPage.tsx
// 登録済みユーザーの一覧・詳細画面。
// 「どう使われているかを知ることは今後のサービスの向上に不可欠」という方針のもと、
// まずは可視化（一覧・詳細のメタデータ表示）のみを実装する。無効化/削除等の操作系は
// 今回のスコープ外（別途着手予定）。

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useAuth } from '../../context/AuthContext';
import { useAppTokens } from '../../context/ThemeModeContext';
import { NEW_API } from '../../config/api';
import AdminNav from './AdminNav';

interface UserListItem {
  user_sub: string;
  username: string;
  email: string | null;
  email_verified: boolean;
  status: string;
  enabled: boolean;
  created_at: string | null;
  updated_at: string | null;
  book_count: number;
}

interface UserDetail extends UserListItem {
  withdrawn_book_count: number;
  shelf_count: number;
  list_count: number;
  highlight_book_count: number;
  highlight_count: number;
  favorite_count: number;
  first_book_registered_at: string | null;
  last_book_registered_at: string | null;
}

const AdminUsersPage: React.FC = () => {
  const TOKENS = useAppTokens();
  const { idToken } = useAuth();

  const [users, setUsers] = useState<UserListItem[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');

  const [selectedUsername, setSelectedUsername] = useState<string | null>(null);
  const [detail, setDetail] = useState<UserDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const cardSx = {
    border: `1px solid ${TOKENS.border}`,
    background: TOKENS.glass,
    borderRadius: '18px',
    p: 2.5,
  } as const;

  const inputSx = {
    '& .MuiOutlinedInput-root': { color: TOKENS.text, background: TOKENS.buttonBg, borderRadius: '12px' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: TOKENS.borderSoft },
    '& .MuiInputLabel-root': { color: TOKENS.textSub },
  } as const;

  const fetchUsers = useCallback(async () => {
    if (!idToken) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(
        NEW_API.ADMIN,
        { operation_type: 'list_users' },
        { headers: { Authorization: `Bearer ${idToken}` } },
      );
      setUsers(res.data?.data ?? []);
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || 'ユーザー一覧の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [idToken]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const openDetail = useCallback(
    async (username: string) => {
      if (!idToken) return;
      setSelectedUsername(username);
      setDetail(null);
      setDetailError(null);
      setDetailLoading(true);
      try {
        const res = await axios.post(
          NEW_API.ADMIN,
          { operation_type: 'get_user_detail', username },
          { headers: { Authorization: `Bearer ${idToken}` } },
        );
        setDetail(res.data?.data ?? null);
      } catch (e: any) {
        setDetailError(e?.response?.data?.error?.message || 'ユーザー詳細の取得に失敗しました');
      } finally {
        setDetailLoading(false);
      }
    },
    [idToken],
  );

  const closeDetail = () => {
    setSelectedUsername(null);
    setDetail(null);
    setDetailError(null);
  };

  const filteredUsers = useMemo(() => {
    if (!users) return [];
    const q = searchText.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) =>
        u.username.toLowerCase().includes(q) ||
        (u.email ?? '').toLowerCase().includes(q) ||
        u.user_sub.toLowerCase().includes(q),
    );
  }, [users, searchText]);

  const formatDateTime = (iso: string | null) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString('ja-JP');
    } catch {
      return iso;
    }
  };

  const detailRow = (label: string, value: React.ReactNode) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75, borderBottom: `1px solid ${TOKENS.border}` }}>
      <Typography sx={{ fontSize: 12.5, color: TOKENS.textSub }}>{label}</Typography>
      <Typography sx={{ fontSize: 12.5, color: TOKENS.text, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100dvh', background: TOKENS.bg, color: TOKENS.text }}>
      <AdminNav />
      <Box sx={{ maxWidth: 900, margin: 'auto', px: 2, py: 4 }}>
        {selectedUsername ? (
          <Box>
            <Button onClick={closeDetail} sx={{ mb: 1.5, textTransform: 'none', color: TOKENS.textSub, pl: 0 }}>
              ← 一覧に戻る
            </Button>

            {detailError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {detailError}
              </Alert>
            )}

            {detailLoading || !detail ? (
              <CircularProgress size={22} sx={{ color: TOKENS.text }} />
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={cardSx}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Typography sx={{ fontSize: 18, fontWeight: 700 }}>{detail.username}</Typography>
                    <Chip
                      label={detail.enabled ? detail.status : '無効化済み'}
                      size="small"
                      sx={{
                        fontSize: 11,
                        background: detail.enabled ? '#1b5e20' : '#5c1a1a',
                        color: '#fff',
                      }}
                    />
                  </Box>
                  {detailRow('user_sub', <Box component="span" sx={{ fontFamily: 'monospace', fontSize: 11 }}>{detail.user_sub}</Box>)}
                  {detailRow('メールアドレス', `${detail.email ?? '—'}${detail.email_verified ? '（確認済み）' : '（未確認）'}`)}
                  {detailRow('Cognito登録日', formatDateTime(detail.created_at))}
                  {detailRow('Cognito最終更新', formatDateTime(detail.updated_at))}
                </Box>

                <Box sx={cardSx}>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1 }}>利用状況</Typography>
                  {detailRow('登録冊数（除籍済みを除く）', detail.book_count.toLocaleString('ja-JP'))}
                  {detailRow('除籍済み冊数', detail.withdrawn_book_count.toLocaleString('ja-JP'))}
                  {detailRow('本棚数', detail.shelf_count.toLocaleString('ja-JP'))}
                  {detailRow('リスト数', detail.list_count.toLocaleString('ja-JP'))}
                  {detailRow('抜粋・メモ登録本数', detail.highlight_book_count.toLocaleString('ja-JP'))}
                  {detailRow('抜粋・メモ件数', detail.highlight_count.toLocaleString('ja-JP'))}
                  {detailRow('お気に入り件数', detail.favorite_count.toLocaleString('ja-JP'))}
                  {detailRow('初回登録日時', formatDateTime(detail.first_book_registered_at))}
                  {detailRow('最終登録日時', formatDateTime(detail.last_book_registered_at))}
                </Box>
              </Box>
            )}
          </Box>
        ) : (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Typography sx={{ fontSize: 22, fontWeight: 700 }}>ユーザー</Typography>
              <Button
                onClick={fetchUsers}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={14} sx={{ color: TOKENS.textSub }} /> : <RefreshIcon />}
                sx={{ textTransform: 'none', color: TOKENS.textSub }}
              >
                更新
              </Button>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Typography sx={{ fontSize: 12.5, color: TOKENS.textSub, mb: 2 }}>
              登録冊数が多い順に並んでいます。クリックすると詳細（利用状況）を確認できます。
            </Typography>

            <TextField
              size="small"
              placeholder="ユーザー名・メールアドレス・user_subで絞り込み"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              fullWidth
              sx={{ ...inputSx, mb: 2 }}
            />

            {loading && !users ? (
              <CircularProgress size={22} sx={{ color: TOKENS.text }} />
            ) : (
              <TableContainer sx={{ ...cardSx, p: 0 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: TOKENS.textSub, fontSize: 12, borderColor: TOKENS.border }}>ユーザー名</TableCell>
                      <TableCell sx={{ color: TOKENS.textSub, fontSize: 12, borderColor: TOKENS.border }}>メールアドレス</TableCell>
                      <TableCell sx={{ color: TOKENS.textSub, fontSize: 12, borderColor: TOKENS.border }}>状態</TableCell>
                      <TableCell sx={{ color: TOKENS.textSub, fontSize: 12, borderColor: TOKENS.border }} align="right">
                        登録冊数
                      </TableCell>
                      <TableCell sx={{ color: TOKENS.textSub, fontSize: 12, borderColor: TOKENS.border }}>登録日</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredUsers.map((u) => (
                      <TableRow
                        key={u.user_sub}
                        hover
                        onClick={() => openDetail(u.username)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell sx={{ color: TOKENS.text, fontSize: 12.5, borderColor: TOKENS.border }}>{u.username}</TableCell>
                        <TableCell sx={{ color: TOKENS.textSub, fontSize: 12.5, borderColor: TOKENS.border }}>{u.email ?? '—'}</TableCell>
                        <TableCell sx={{ borderColor: TOKENS.border }}>
                          <Chip
                            label={u.enabled ? u.status : '無効化済み'}
                            size="small"
                            sx={{
                              fontSize: 10.5,
                              height: 20,
                              background: u.enabled ? TOKENS.buttonBg : '#5c1a1a',
                              color: u.enabled ? TOKENS.text : '#fff',
                            }}
                          />
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ color: TOKENS.text, fontSize: 12.5, borderColor: TOKENS.border, fontVariantNumeric: 'tabular-nums' }}
                        >
                          {u.book_count.toLocaleString('ja-JP')}
                        </TableCell>
                        <TableCell sx={{ color: TOKENS.textSub, fontSize: 11.5, borderColor: TOKENS.border }}>
                          {formatDateTime(u.created_at)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {users && (
              <Typography sx={{ fontSize: 11.5, color: TOKENS.textSub, mt: 1.5 }}>
                {filteredUsers.length} / {users.length} 人
              </Typography>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default AdminUsersPage;
